/**
 * Live paper formatters for DRAFT contract / proposal / client About.
 * Display-only: never invents a Project ID or a sale.
 */

import { fieldCodeFromProjectLabel, toFieldCode } from '@/utils/projectFieldCode';

const ALREADY_FIELD_CODE = /^P\d{2}-\d{1,4}$/i;
const EXTENT_VALUE_RE = /^(none|partial|full)(?:\s+demolition)?$/i;
const EXTENT_LABEL_RE = /^(specify\s+)?extent( of demolition)?$/i;

export function formatPaperProjectId(input: {
  fieldCode?: string | null;
  projectNumber?: string | null;
  projectDocumentId?: string | null;
  projectLabel?: string | null;
  contractId?: string | null;
}): string {
  const fieldCode = input.fieldCode?.trim();
  if (fieldCode) return fieldCode;

  const candidates = [input.projectNumber, input.projectDocumentId, input.projectLabel];
  for (const raw of candidates) {
    const candidate = raw?.trim();
    if (!candidate) continue;
    if (ALREADY_FIELD_CODE.test(candidate)) return candidate;
    const converted = toFieldCode(candidate) || fieldCodeFromProjectLabel(candidate);
    if (converted) return converted;
  }
  for (const raw of candidates) {
    const candidate = raw?.trim();
    if (candidate) return candidate;
  }
  return input.contractId?.trim() || '';
}

function isExtentValue(value?: string | null): boolean {
  if (!value) return false;
  return EXTENT_VALUE_RE.test(value.trim().replace(/_/g, ' '));
}

function titleExtentValue(value: string): string {
  const v = value.trim().replace(/_/g, ' ');
  if (/^partial(?:\s+demolition)?$/i.test(v)) return 'Partial';
  if (/^full(?:\s+demolition)?$/i.test(v)) return 'Full';
  if (/^none(?:\s+demolition)?$/i.test(v)) return 'None';
  return v;
}

/** Prefer CSF contractLabel, then scfLabel / displayLabel. */
export function paperCsfLabel(field: {
  contractLabel?: string | null;
  scfLabel?: string | null;
  displayLabel?: string | null;
}): string | null {
  const label =
    field.contractLabel?.trim() ||
    field.scfLabel?.trim() ||
    field.displayLabel?.trim() ||
    null;
  return label || null;
}

/**
 * Leftover seed labels: "Extent" and a PARTIAL/FULL "Demolition" row
 * print as the intake/CSF contract label "Extent of Demolition".
 */
export function formatPaperFieldLabel(label: string, value?: string | null): string {
  const l = label.trim();
  if (EXTENT_LABEL_RE.test(l)) return 'Extent of Demolition';
  if (/^demolition$/i.test(l) && isExtentValue(value)) return 'Extent of Demolition';
  return l;
}

export function formatPaperFieldValue(label: string, value: string): string {
  const nextLabel = formatPaperFieldLabel(label, value);
  if (EXTENT_LABEL_RE.test(nextLabel) || (EXTENT_LABEL_RE.test(label) && isExtentValue(value))) {
    return titleExtentValue(value);
  }
  if (/^demolition$/i.test(label) && isExtentValue(value)) return titleExtentValue(value);
  return value;
}

export function formatPaperDetailRow(label: string, value: string): [string, string] {
  const nextLabel = formatPaperFieldLabel(label, value);
  return [nextLabel, formatPaperFieldValue(label, value)];
}

function uniqueLabels(labels: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of labels) {
    const label = raw?.trim();
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(label);
  }
  return out;
}

function stripLabelPrefix(text: string, label: string): string | null {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = text.match(new RegExp(`^${escaped}\\s*[:—–-]\\s*`, 'i'));
  if (!match) return null;
  const stripped = text.slice(match[0].length).trim();
  return stripped !== '' ? stripped : text;
}

/**
 * Split "Label: value" against the display label and leftover aliases
 * ("Extent" vs "Extent of Demolition") so DRAFT paper can use live CSF
 * contractLabel without leaving a duplicated prefix in the value cell.
 */
export function splitPaperStatementLabel(
  text: string,
  fieldLabel: string | null,
  aliases: Array<string | null | undefined> = [],
): { label: string | null; value: string } {
  const t = String(text ?? '').trim();
  const display = fieldLabel?.trim() || null;
  const leftoverExtent =
    EXTENT_LABEL_RE.test(display || '') ||
    /^demolition$/i.test(display || '') ||
    aliases.some((alias) => EXTENT_LABEL_RE.test((alias || '').trim()) || /^demolition$/i.test((alias || '').trim())) ||
    /^extent\b/i.test(t);
  const prefixes = uniqueLabels(
    leftoverExtent
      ? [display, ...aliases, 'Extent of Demolition', 'Specify Extent of Demolition', 'Extent']
      : [display, ...aliases],
  );

  for (const prefix of prefixes) {
    const stripped = stripLabelPrefix(t, prefix);
    if (stripped == null) continue;
    const nextLabel = formatPaperFieldLabel(display || prefix, stripped);
    return { label: nextLabel, value: formatPaperFieldValue(display || prefix, stripped) };
  }

  if (display) {
    const nextLabel = formatPaperFieldLabel(display, t);
    return { label: nextLabel, value: formatPaperFieldValue(display, t) };
  }

  const generic = t.match(/^(.+?)\s*(?:—|–|:)\s+(.+)$/);
  if (generic) {
    const nextLabel = formatPaperFieldLabel(generic[1], generic[2]);
    return { label: nextLabel, value: formatPaperFieldValue(generic[1], generic[2]) };
  }
  return { label: null, value: t };
}
